<template>
  <v-sheet class="d-flex flex-column rounded white">
    <p class="font-weight-500 font-size-18 pa-0">
      {{ $t('ai_features.token_assignment_panel') }}
    </p>

    <div class="mt-6">
      <span
        class="body-2 grey--text text--darken-3"
        v-html="
          $t('ai_features.token_assignment_organization', {
            organizationName,
          })
        "
      />
    </div>

    <v-form v-model="isFormValid" ref="assignmentForm" class="mt-4">
      <v-row class="mt-6 align-start" dense>
        <v-col cols="3">
          <span class="font-bold body-2 grey--text text--darken-3">
            {{ $t('ai_features.tokens_to_assign') }}*
          </span>
        </v-col>

        <v-col cols="5">
          <HumanizedNumericTextField
            class="ma-0 pa-0"
            :allowDecimals="false"
            :value="totalTokensToAssign"
            :vuetifyTextFieldProps="{
              dense: true,
              disabled: isLoading,
              outlined: true,
              rules: [
                _humanizedTotalTokens => {
                  return (
                    this.totalTokensToAssign > 0 ||
                    $t('ai_features.token_assignment_minimum_value_error')
                  )
                },
              ],
            }"
            @change="totalTokensToAssign = $event"
          />
        </v-col>
      </v-row>

      <v-row class="mt-6 align-start" dense>
        <v-col cols="3">
          <span class="font-bold body-2 grey--text text--darken-3">
            {{ $t('ai_features.tracking_number') }}
          </span>
        </v-col>
        <v-col cols="5">
          <v-text-field
            v-model="trackingNumber"
            class="ma-0 pa-0"
            data-private
            data-testid="ai-settings-tracking-number"
            dense
            outlined
            persistent-hint
            :disabled="isLoading"
            :hint="$t('ai_features.tracking_number_hint')"
          />
        </v-col>
      </v-row>

      <div class="d-flex justify-end mt-4">
        <Button
          plain
          color="primary"
          :disabled="!this.isFormValid"
          :text="$t('ai_features.assign_tokens')"
          :loading="isLoading"
          @click="validateForm"
        />
      </div>
    </v-form>

    <TokenAssignmentConfirmation
      :shown="isConfirmationShown"
      :totalTokensToAssign="totalTokensToAssign"
      :trackingNumber="trackingNumber"
      :organizationName="organizationName"
      @confirm="handleConfirmation"
      @cancel="handleHideConfirmation"
    />
  </v-sheet>
</template>
<script>
import HumanizedNumericTextField from '@/components/UI/HumanizedNumericTextField.vue'
import Button from '@/components/UI/Button.vue'
import TokenAssignmentConfirmation from './TokenAssignmentConfirmation.vue'

import { mapGetters } from 'vuex'
import mixin from '@/mixins/mixin.vue'
import api from '@/api'

export default {
  name: 'TokenAssignmentPanel',

  mixins: [mixin],

  props: {
    organization: {
      type: Object,
      required: true,
    },
  },

  components: {
    Button,
    HumanizedNumericTextField,
    TokenAssignmentConfirmation,
  },

  data() {
    return {
      isFormValid: false,
      isConfirmationShown: false,
      totalTokensToAssign: 0,
      trackingNumber: '',
      isLoading: false,
    }
  },

  computed: {
    ...mapGetters(['account']),

    organizationName() {
      return this.organization?.name || ''
    },
  },

  methods: {
    validateForm() {
      this.$refs.assignmentForm.validate()

      if (this.isFormValid) {
        this.isConfirmationShown = true
      }
    },

    async handleConfirmation() {
      this.isConfirmationShown = false
      this.isLoading = true
      try {
        await api.ai.depositTokens(
          this.organization._id,
          this.totalTokensToAssign,
          this.trackingNumber
        )

        this.showSuccess(this.$t('ai_features.token_assignment_success'))

        this.totalTokensToAssign = 0
        this.trackingNumber = ''

        this.$refs.assignmentForm.resetValidation()
        this.isLoading = false

        this.$emit('handleTokensUpdated')
      } catch (error) {
        this.showError(this.$t('ai_features.token_assignment_api_error'))
        this.isLoading = false
      }
    },

    handleHideConfirmation() {
      this.isConfirmationShown = false
    },
  },
}
</script>
